import { BRAND_SPECTRUMS, HIGHLIGHT, hexToOklch, oklchToHex } from './colour-twins.js';

/**
 * F-0579 (`docs/concept-colour-accents.md`): the *Accents* setting and the colours Colour adds.
 *
 * Quiet draws today's app. Colour adds the Liquid Frame as foreground colour only (ACC-2, ACC-5):
 * backgrounds never change, and no colour here ever fills one. The web's `index.css` states these
 * values as `--evx-` tokens under `[data-accents='colour']`; `colourAccents.guard.test.ts` holds the
 * two to each other and computes every contrast the concept measured (ACC-7, ACC-13).
 */

export const ACCENTS = {
  QUIET: 'quiet',
  COLOUR: 'colour',
} as const;

export type AccentsType = (typeof ACCENTS)[keyof typeof ACCENTS];

/** Our default (a setting, not a decision): Colour. Backgrounds do not change, so the risk is small. */
export const DEFAULT_ACCENTS: AccentsType = ACCENTS.COLOUR;

export function isAccents(value: unknown): value is AccentsType {
  return value === ACCENTS.QUIET || value === ACCENTS.COLOUR;
}

/** `users.preferences.appearance`, and `GET/PATCH /user-preferences/appearance`. */
export interface IAppearancePreferences {
  accents: AccentsType;
}

export const DEFAULT_APPEARANCE_PREFERENCES: IAppearancePreferences = {
  accents: DEFAULT_ACCENTS,
};

/** The four Liquid Frame stops, teal to azure: the gradient a hairline or an edge draws (ACC-5, ACC-6). */
export const ACCENT_FRAME_STOPS: readonly string[] = BRAND_SPECTRUMS.find(
  (spectrum) => spectrum.id === 'liquid-frame',
)!.stops.map((stop) => stop.original);

export type AccentThemeType = 'light' | 'dark';

/** One look's foreground colours, in the order `index.css` states them as tokens. */
export interface IAccentColours {
  /** ACC-5: the frame blue, the shell's `HIGHLIGHT`. Links, focus ring, selected edges, counts. */
  highlight: string;
  /** ACC-4: one frame stop per navigation icon, walking teal to azure; each ≥ 3:1 on the sidebar. */
  iconStops: readonly [string, string, string, string];
  /** ACC-6: a button's icon, on the primary fill (graphite in light, a light neutral in dark) and the secondary. */
  buttonIcon: { primary: string; secondary: string };
  /** ACC-7: a link inside running text — ≥ 3:1 to the words around it, ≥ 4.5:1 to the ground. */
  proseLink: string;
  /**
   * ACC-5: a count's number (a sidebar badge's waiting count): the frame blue, a tone darker in
   * light so it reads 4.5:1 on the badge's own grey (`#DDE0E2`, 4.92:1); dark keeps the highlight.
   */
  count: string;
}

/** ACC-7, Quiet: a prose link is a grey that still differs from the text by 3:1 (WCAG 1.4.1). */
export const QUIET_PROSE_LINK: Record<AccentThemeType, string> = {
  light: '#5F6670',
  dark: '#8C949B',
};

export const COLOUR_ACCENTS: Record<AccentThemeType, IAccentColours> = {
  light: {
    highlight: HIGHLIGHT.light,
    iconStops: ['#1F8C83', '#1593A4', '#1F74BD', '#3962D8'],
    buttonIcon: { primary: '#7CC4FF', secondary: '#3962D8' },
    proseLink: '#355FD7',
    count: '#2F55C4',
  },
  dark: {
    highlight: HIGHLIGHT.dark,
    iconStops: ['#45C2B6', '#3FCBE0', '#4FADFF', '#7D9BFF'],
    // The dark primary is a light fill (ruling 4), where the frame blue is 1.34:1: its icon takes
    // the label's colour (14.48:1).
    buttonIcon: { primary: '#171717', secondary: '#4FADFF' },
    proseLink: '#4B97E2',
    count: '#4FADFF',
  },
};

/** ACC-4: the redraw on mouse-over, once, as Relay's runner traces the frame. */
export const ACCENT_ICON_TRACE_MS = 700;
/** ACC-6: the hairline's fade-in on hover. */
export const ACCENT_HAIRLINE_FADE_MS = 200;

/**
 * ACC-4: which frame stop a navigation icon takes by its place in the list, walking teal to azure
 * and back a step, as the waiting studies do (the prototype's order). Stops are 1-4.
 */
export const ACCENT_ICON_WALK: readonly (1 | 2 | 3 | 4)[] = [1, 2, 2, 3, 3, 4, 4, 3];

export function accentIconStop(position: number): 1 | 2 | 3 | 4 {
  const at = Math.max(0, Math.floor(position)) % ACCENT_ICON_WALK.length;
  return ACCENT_ICON_WALK[at];
}

/** ACC-4e: how many icon stops the gradient walks through. */
const ICON_STOPS = 4;

/**
 * ACC-4e (I-1818): where icon `position` of `count` sits on the one gradient, 0 at the first stop
 * and 1 at the last, as the stop it starts from (0-based) and how far it is towards the next.
 */
function walkPlace(position: number, count: number): { from: number; towards: number } {
  const at = count <= 1 ? 0 : Math.min(1, Math.max(0, position / (count - 1)));
  const scaled = at * (ICON_STOPS - 1);
  const from = Math.min(ICON_STOPS - 2, Math.floor(scaled));
  return { from, towards: scaled - from };
}

/**
 * ACC-4e: icon `position` of `count`'s colour, as CSS over the theme's own stop tokens, so light and
 * dark each mix their own stops: `color-mix(in oklch, …)` between the two stops either side.
 */
export function accentWalkColour(position: number, count: number): string {
  const { from, towards } = walkPlace(position, count);
  const share = Math.round((1 - towards) * 1000) / 10;
  if (share >= 100) return `var(--evx-icon-stop-${from + 1})`;
  if (share <= 0) return `var(--evx-icon-stop-${from + 2})`;
  return `color-mix(in oklch, var(--evx-icon-stop-${from + 1}) ${share}%, var(--evx-icon-stop-${from + 2}))`;
}

/**
 * ACC-4e: the same colour as `#RRGGBB`, for the theme's stops, mixed in OKLCH along the shorter hue
 * arc as `color-mix` mixes: what the contrast test measures.
 */
export function accentWalkHex(position: number, count: number, theme: AccentThemeType): string {
  const { from, towards } = walkPlace(position, count);
  const stops = COLOUR_ACCENTS[theme].iconStops;
  const [a, b] = [hexToOklch(stops[from]), hexToOklch(stops[from + 1])];
  const turn = ((b.h - a.h + 540) % 360) - 180;
  return oklchToHex(
    a.l + (b.l - a.l) * towards,
    a.c + (b.c - a.c) * towards,
    (a.h + turn * towards + 360) % 360,
  );
}
